import type { CvKey } from "../../data/profileData";

/**
 * The CvFinder decision tree. Two decisions, at most:
 *
 *   1. skills — the skill area the recruiter is looking for (5 choices).
 *      Framed as the recruiter's NEED ("what are you looking for?"), not as a
 *      menu of Olga's professional identities — a hiring manager should never
 *      have to scan five job titles she could hold.
 *   2. specialisation — the exact angle inside it (2–3 choices).
 *
 * Each leaf points at exactly ONE CvKey, so the recruiter is always handed a
 * single, unambiguous CV. A couple of CVs are reachable from two skill areas
 * (e.g. the AI-first designer sits under both Design and AI) — that is fine:
 * both paths still resolve to one document.
 *
 * The role labels come from Olga's 11 CVs (see build/olga_cv_skills.html);
 * the hints are written for the hiring manager, not for Olga.
 */
export type CvSpecialisation = {
  cv: CvKey;
  label: string;
  /** One line, in the hiring manager's terms, on what this angle covers. */
  hint: string;
};

export type CvSkillArea = {
  value: string;
  label: string;
  /** Concrete skills in this area — what the recruiter is scanning for. */
  skills: string;
  options: CvSpecialisation[];
};

export const CV_SKILL_AREAS: CvSkillArea[] = [
  {
    value: "design",
    label: "Someone to design the experience",
    skills: "UX research · UI · design systems · Figma · prototyping · accessibility",
    options: [
      {
        cv: "product-designer",
        label: "A designer who owns the product's shape",
        hint: "Research, flows and interface — the whole UX from question to screen.",
      },
      {
        cv: "design-engineer",
        label: "A designer who ships the front-end",
        hint: "Designs it, then builds the accessible, responsive front-end themselves.",
      },
      {
        cv: "product-designer-ai-first",
        label: "A designer who moves fast with AI",
        hint: "Design judgement kept, with AI tooling for speed and rapid prototyping.",
      },
    ],
  },
  {
    value: "engineering",
    label: "Someone to build the software",
    skills: "React · TypeScript · Node.js · Python · REST APIs · databases",
    options: [
      {
        cv: "frontend-developer",
        label: "A developer who builds your designs properly",
        hint: "You have the designs; they turn them into accessible, secure front-end.",
      },
      {
        cv: "product-engineer",
        label: "A builder who owns it end-to-end",
        hint: "Front-end, back-end and the API calls in between, owned as one.",
      },
      {
        cv: "junior-software-engineer",
        label: "A full-stack engineer for general software",
        hint: "General software delivery across JS/TS, Python, React and Node.",
      },
    ],
  },
  {
    value: "ai",
    label: "Someone to add AI & automation",
    skills: "Prompt engineering · AI features · automation · rapid prototyping · generative media",
    options: [
      {
        cv: "ai-solutions-automation",
        label: "A builder who ships AI features & automation",
        hint: "AI features or internal automation scoped, built and shipped.",
      },
      {
        cv: "product-designer-ai-first",
        label: "A designer who leads with AI",
        hint: "AI-assisted product design and prototyping, design judgement intact.",
      },
    ],
  },
  {
    value: "support",
    label: "Someone to support your people",
    skills: "Help desk · troubleshooting · ticketing · knowledge base · technical writing",
    options: [
      {
        cv: "customer-support",
        label: "Someone who supports your customers",
        hint: "Chat & email support with real product understanding — bugs reproduced, not just logged.",
      },
      {
        cv: "it-support",
        label: "Someone who keeps your users unblocked",
        hint: "Troubleshooting, ticketing and plain-language help for users.",
      },
    ],
  },
  {
    value: "security",
    label: "Someone to keep it secure & compliant",
    skills: "IAM · access control · GDPR/AVG · threat detection · secure-by-design",
    options: [
      {
        cv: "cybersecurity-analyst",
        label: "An analyst who watches the front line",
        hint: "Entry-level security: IAM, access control and threat-detection fundamentals.",
      },
    ],
  },
];
